import apiClient from '@/utility/apiClient'
import { handleApiError } from '@/lib/api/utils/errorHandler'
import { RecommendedResponse, PageMeta, Tag } from '@/lib/api/types/api'
import { Article } from '@/types/article'
import { Company } from '@/types/company'
import { RecommendedContent } from '@/types/recommended'

/**
 * Recommended API service
 * Based on the Rails routes.rb file
 * Implements the /api/recommended endpoints
 */
export const RecommendedService = {
  /**
   * Get recommended contents
   * Implements the GET /api/recommended endpoint
   * @param params Optional query parameters
   */
  getRecommendedContents: async (
    params: Record<string, string | number> = {},
  ): Promise<RecommendedResponse> => {
    try {
      const url = 'recommended'
      const response = await apiClient.request('GET', url, { params })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get recommended stories
   */
  getRecommendedStories: async (
    page: number = 1,
    categoryIds: string[] = [],
  ): Promise<{ records: Article[]; pageMeta: PageMeta }> => {
    try {
      // Format category IDs as needed by the API
      const params: { page: number; 'category_ids[]'?: string[] } = { page }

      // Add category IDs in the format expected by the API
      if (categoryIds.length > 0) {
        params['category_ids[]'] = categoryIds
      }

      const url = 'recommended_stories'
      const response = await apiClient.request('GET', url, {
        params: params as unknown as Record<string, string | number>,
      })

      return {
        records: response.data,
        pageMeta: {
          currentPage: page,
          totalPages: 1,
          totalContent: response.data.length,
        },
      }
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get AI recommended contents
   * Implements the recommended_contents/ai endpoint
   * Returns empty array if no data available (no retries or timeouts)
   */
  getAIRecommendedContents: async (
    params: Record<string, string | number> = {},
  ): Promise<RecommendedContent[]> => {
    try {
      const url = 'recommended_contents/ai'
      const response = await apiClient.request('GET', url, { params })

      // Handle response data properly
      const data = response.data || []

      // Ensure we always return an array
      let result: RecommendedContent[] = []

      // Check if data is already an array
      if (Array.isArray(data)) {
        result = data
      }
      // Check if data has an array property (e.g., data.items, data.contents, data.recommendations)
      else if (data && typeof data === 'object') {
        // Try common property names that might contain the array
        // Primary property from API (camelCase converted)
        if (Array.isArray(data.aiRecommendedContents)) {
          result = data.aiRecommendedContents
        } else if (Array.isArray(data.ai_recommended_contents)) {
          result = data.ai_recommended_contents
        } else if (Array.isArray(data['ai-recommended-contents'])) {
          result = data['ai-recommended-contents']
        } else if (Array.isArray(data.items)) {
          result = data.items
        } else if (Array.isArray(data.contents)) {
          result = data.contents
        } else if (Array.isArray(data.recommendations)) {
          result = data.recommendations
        } else if (Array.isArray(data.recommended_contents)) {
          result = data.recommended_contents
        } else {
          // If no array found, log the structure for debugging (only if data is not empty)
          if (data !== null && data !== undefined && data !== '' && JSON.stringify(data) !== '[]') {
            console.warn(
              '🚨 RecommendedService.getAIRecommendedContents: Unexpected response structure',
              {
                dataType: typeof data,
                dataKeys: Object.keys(data),
                data: JSON.stringify(data).substring(0, 200), // First 200 chars for debugging
              },
            )
          }
        }
      }

      return result
    } catch (error) {
      // Return empty array instead of throwing error
      return []
    }
  },

  /**
   * Get recommended tags
   * Implements the recommended_tags endpoint
   */
  getRecommendedTags: async (params: Record<string, string | number> = {}): Promise<Tag[]> => {
    try {
      const url = 'recommended_tags'
      const response = await apiClient.request('GET', url, { params })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get recommended companies
   * Implements the recommended_companies endpoint
   */
  getRecommendedCompanies: async (
    params: Record<string, string | number> = {},
  ): Promise<Company[]> => {
    try {
      const url = 'recommended_companies'
      const response = await apiClient.request('GET', url, { params })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },
}
